import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../config/runtime';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface TrapRentalRequest {
  id: string;
  businessId: string;
  businessName: string;
  subscriptionTypeId: string;
  subscriptionTypeName: string;
  planFamily: string;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedByUserEmail: string;
  startDate: string;
  endDate: string;
  estimatedCost: number;
  status: string;
  adminNotes: string | null;
  reviewedByUserName: string | null;
  reviewedDate: string | null;
  dateCreated: string;
}

const STATUS_OPTIONS = ['Pending', 'Contacted', 'Fulfilled', 'Cancelled'] as const;

const statusColor = (status: string): 'warning' | 'info' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'Pending': return 'warning';
    case 'Contacted': return 'info';
    case 'Fulfilled': return 'success';
    case 'Cancelled': return 'error';
    default: return 'default';
  }
};

const TrapRentalRequestsManagement: React.FC = () => {
  const auth = useAuth();
  const apiUrl = getApiBaseUrl();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [requests, setRequests] = useState<TrapRentalRequest[]>([]);
  const [filter, setFilter] = useState<'Pending' | 'All'>('Pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const [statusTarget, setStatusTarget] = useState<TrapRentalRequest | null>(null);
  const [newStatus, setNewStatus] = useState<string>('Contacted');
  const [adminNotes, setAdminNotes] = useState('');

  const fetchRequests = async (statusFilter: 'Pending' | 'All') => {
    setLoading(true);
    try {
      const url = statusFilter === 'Pending'
        ? `${apiUrl}/api/trap-rental-requests?status=Pending`
        : `${apiUrl}/api/trap-rental-requests`;
      const response = await fetch(url, { headers: authHeader });
      if (!response.ok) throw new Error('Failed to fetch trap rental requests');
      const data = await response.json();
      setRequests(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.isSignedIn) fetchRequests(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSignedIn, filter]);

  const openStatusDialog = (request: TrapRentalRequest) => {
    setStatusTarget(request);
    setNewStatus(request.status === 'Pending' ? 'Contacted' : request.status);
    setAdminNotes(request.adminNotes || '');
  };

  const handleUpdateStatus = async () => {
    if (!statusTarget) return;
    setActioningId(statusTarget.id);
    try {
      const response = await fetch(`${apiUrl}/api/trap-rental-requests/${statusTarget.id}/status`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, adminNotes: adminNotes || undefined }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update request');
      }
      setStatusTarget(null);
      await fetchRequests(filter);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setActioningId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Trap Rental Requests</Typography>
        <ToggleButtonGroup value={filter} exclusive size="small" onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="Pending">Pending</ToggleButton>
          <ToggleButton value="All">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No requests found</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {requests.map((req) => (
            <Card key={req.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{req.businessName}</Typography>
                  <Chip label={req.status} size="small" color={statusColor(req.status)} />
                </Box>
                <Typography variant="body2">{req.subscriptionTypeName}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {new Date(req.startDate).toLocaleDateString()} – {new Date(req.endDate).toLocaleDateString()} · est. ${req.estimatedCost.toFixed(2)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="body2">{req.requestedByUserName}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">{req.requestedByUserEmail}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Requested {new Date(req.dateCreated).toLocaleDateString()}
                </Typography>
                {req.adminNotes && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Notes: {req.adminNotes}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  variant="contained"
                  disabled={actioningId === req.id}
                  onClick={() => openStatusDialog(req)}
                >
                  Update Status
                </Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Est. Cost</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell>{req.businessName}</TableCell>
                  <TableCell>{req.subscriptionTypeName}</TableCell>
                  <TableCell>
                    {new Date(req.startDate).toLocaleDateString()} – {new Date(req.endDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>${req.estimatedCost.toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{req.requestedByUserName}</Typography>
                    <Typography variant="caption" color="text.secondary">{req.requestedByUserEmail}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={req.status} size="small" color={statusColor(req.status)} />
                    {req.adminNotes && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {req.adminNotes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={actioningId === req.id}
                      onClick={() => openStatusDialog(req)}
                    >
                      Update Status
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!statusTarget} onClose={() => setStatusTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Request — {statusTarget?.businessName}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Admin Notes (optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateStatus}
            disabled={actioningId === statusTarget?.id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrapRentalRequestsManagement;
